/**
 * TechDebugFooter — Compact tech metrics under message (BLUE mode only)
 *
 * Shows model, latency, TTS, video info as a one-liner.
 * Expandable to full details from message.debugInfo.
 * Only visible in advanced (BLUE) debug mode.
 */

import { useState } from 'react'
import { clsx } from 'clsx'
import type { MessageDebugInfo } from '../../stores/chatStore'

interface TechDebugFooterProps {
  debugInfo: MessageDebugInfo
}

export default function TechDebugFooter({ debugInfo }: TechDebugFooterProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  const { generation, tts, video } = debugInfo

  // Build compact summary parts
  const parts: string[] = []

  if (generation?.model) {
    const model = generation.model.includes('/') ? generation.model.split('/').pop()! : generation.model
    parts.push(`🤖 ${model}`)
  }
  if (generation?.latency_ms) {
    parts.push(`${(generation.latency_ms / 1000).toFixed(1)}s`)
  }
  if (tts?.provider) {
    const ttsDur = tts.generation_ms ? ` ${(tts.generation_ms / 1000).toFixed(1)}s` : ''
    parts.push(`TTS: ${tts.provider}${ttsDur}`)
  }
  if (video?.provider) {
    const vidDur = video.generation_ms ? ` ${(video.generation_ms / 1000).toFixed(1)}s` : ''
    parts.push(`Video: ${video.provider}${vidDur}`)
  }
  if (generation?.cache_hit !== undefined) {
    parts.push(`Cache: ${generation.cache_hit ? 'hit' : 'miss'}`)
  }

  if (parts.length === 0) return null

  return (
    <div
      className="mt-2 text-[10px] font-mono text-neutral-400 cursor-pointer hover:text-neutral-500 transition-colors"
      onClick={(e) => {
        e.stopPropagation()
        setIsExpanded(!isExpanded)
      }}
    >
      {/* Compact one-liner */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <span>{parts.join(' | ')}</span>
        <span className="text-[9px]">{isExpanded ? '▼' : '▶'}</span>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="mt-1.5 p-2 rounded bg-neutral-50 border border-neutral-200 text-neutral-600 space-y-1">
          {generation && (
            <Section title="Generation">
              {generation.model && <Row label="Model" value={generation.model} />}
              {generation.tokens_in != null && <Row label="Tokens in" value={String(generation.tokens_in)} />}
              {generation.tokens_out != null && <Row label="Tokens out" value={String(generation.tokens_out)} />}
              {generation.latency_ms != null && <Row label="Latency" value={`${generation.latency_ms}ms`} />}
              {generation.escalated && <Row label="Escalated" value={generation.escalation_reason || 'yes'} />}
              {generation.cache_hit !== undefined && <Row label="Cache" value={generation.cache_hit ? 'hit' : 'miss'} />}
            </Section>
          )}
          {tts && (
            <Section title="TTS">
              {tts.provider && <Row label="Provider" value={tts.provider} />}
              {tts.voice_id && <Row label="Voice" value={tts.voice_id} />}
              {tts.duration_ms != null && <Row label="Duration" value={`${tts.duration_ms}ms`} />}
              {tts.generation_ms != null && <Row label="Gen time" value={`${tts.generation_ms}ms`} />}
            </Section>
          )}
          {video && (
            <Section title="Video">
              {video.provider && <Row label="Provider" value={video.provider} />}
              {video.avatar_id && <Row label="Avatar" value={video.avatar_id} />}
              {video.duration_ms != null && <Row label="Duration" value={`${video.duration_ms}ms`} />}
              {video.generation_ms != null && <Row label="Gen time" value={`${video.generation_ms}ms`} />}
              {video.lipsync_quality != null && <Row label="Lipsync" value={`${(video.lipsync_quality * 100).toFixed(0)}%`} />}
            </Section>
          )}
        </div>
      )}
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="font-semibold text-neutral-500 text-[9px] uppercase tracking-wider">{title}</div>
      {children}
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className={clsx('flex gap-2')}>
      <span className="text-neutral-400 shrink-0">{label}:</span>
      <span className="break-all">{value}</span>
    </div>
  )
}
